import React, { useState, useCallback, useEffect } from 'react';

import PageTemplate from '../../../components/common/template/PageTemplate';
import styled from 'styled-components';

import { ERROR_RETRY } from '../../../lib/Error';
import * as heApi from '../HeApi';
import { ClipLoader } from 'react-spinners';
import { Button } from '../../../components/common/atom/Button';
import { getCDNUrl } from '../../../lib/image/CDNUrl';
import * as PATH from '../../../lib/routes/path';

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

const maskPhone = (phone) => `${phone.slice(0, 3)}-****-${phone.slice(7)}`;

const PHONE_ERROR = '010으로 시작하는 휴대폰 번호 11자리를 입력해 주세요.';

const HePrivacyConsent = ({ workspace }) => {
  const [ isLoading, setIsLoading ] = useState(false);
  const [ step, setStep ] = useState('consent'); // 'consent', 'phone', 'success'
  const [ phoneNumber, setPhoneNumber ] = useState('010-');
  const [ phoneError, setPhoneError ] = useState('');

  const finalProducts =
    workspace.croppedFaces ? workspace.croppedFaces.map(croppedFace => croppedFace.finalProducts).flat() : [];

  const isBabyfaceAvailable =
    finalProducts.some(f => f.productName === "Babyface" || f.originalProductName === "Babyface");

  const [ privacyChecked, setPrivacyChecked ] = useState(false);
  const [ sensitiveChecked, setSensitiveChecked ] = useState(false);
  const [ showTerms, setShowTerms ] = useState(false);
  const allChecked = privacyChecked && sensitiveChecked;
  const hospitalName = (workspace.hospital && workspace.hospital.name) || '병원';
  const phone = phoneNumber.replace(/\D/g, '');

  // 상단 뒤로가기(history.goBack)가 전화번호 단계에서 동의 단계로 돌아오도록 단계마다 history 항목을 쌓는다.
  useEffect(() => {
    const onPopState = () => setStep('consent');
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const handleAllCheck = (checked) => {
    setPrivacyChecked(checked);
    setSensitiveChecked(checked);
  };

  const handleConsentClick = useCallback(() => {
    if (!allChecked) return;
    window.history.pushState(null, '');
    setStep('phone');
    window.scrollTo(0, 0);
  }, [allChecked]);

  const handlePhoneSubmit = useCallback(() => {
    if (!/^010[2-9][0-9]{7}$/.test(phone)) {
      setPhoneError(PHONE_ERROR);
      return;
    }
    setPhoneError('');
    setIsLoading(true);
    heApi.consentPrivacyUsage({ workspaceId: workspace.id, token: sessionStorage.be_token, phone })
      .then(() => {
        setStep('success');
        window.scrollTo(0, 0);
      }).catch((error) => {
        if (error.response && error.response.status === 401) {
          setPhoneError('입력된 정보가 일치하지 않습니다. 다시 한번 확인해 주시기 바랍니다.');
        } else {
          setPhoneError(ERROR_RETRY);
        }
      })
      .finally(() => setIsLoading(false));
  }, [phone, workspace]);

  const hero = isBabyfaceAvailable ? {
    title: '"엄마, 아빠는 너를 이렇게 사랑한단다."',
    lead: '초음파 속 우리 아기, 태어난 모습으로 미리 만나보세요.',
    image: 'babyface_he_img_2.png',
    alt: '베이비페이스 결과 이미지',
  } : {
    title: '소중한 첫 만남을 더욱 특별하게',
    lead: '흐릿한 입체초음파를 선명하고 깨끗하게 다듬어 드려요.',
    image: 'babyface_he_img_1.png',
    alt: '클리어페이스 결과 이미지',
  };

  const benefits = isBabyfaceAvailable ? [
    ['고화질 베이비페이스 이미지', '언제든지 다시 다운로드 가능!'],
    ['배냇짓 동영상 추가 제공', '내 새끼 자랑 이벤트 참가 시'],
  ] : [
    ['고화질 이미지 다운로드 링크', '언제든지 다시 볼 수 있어요!'],
    ['베이비페이스 할인쿠폰'],
  ];

  const deliveries = isBabyfaceAvailable ? benefits : [
    ['클리어페이스 고화질 이미지'],
    ['베이비페이스 할인쿠폰'],
  ];

  if (step === 'consent') {
    return (
      <PageTemplate title={isBabyfaceAvailable ? PAGE_TITLE_BABYFACE : PAGE_TITLE_CLEARFACE}>
        <Hero>
          <Headline>{hero.title}</Headline>
          <Lead>{hero.lead}</Lead>
          <Photo src={getCDNUrl("service", hero.image)} alt={hero.alt} />
        </Hero>
        <SectionTitle>휴대폰으로 이미지 받아보기</SectionTitle>
        <Lead>병원에 저장된 아기 초음파 이미지를 휴대폰으로 안전하게 받아보세요.</Lead>
        <Notes>
          <Warning><InfoIcon /><span>미동의 시: 병원에만 보관 (<strong>90일 후 자동 삭제</strong>)</span></Warning>
          <Warning><InfoIcon /><span>촬영 후 가급적 빨리 받아두세요.</span></Warning>
        </Notes>
        <ConsentCard>
          <ConsentCheck all checked={allChecked}>
            <input type="checkbox" checked={allChecked} onChange={e => handleAllCheck(e.target.checked)} />
            전체 동의
          </ConsentCheck>
          <ConsentCheck>
            <input type="checkbox" checked={sensitiveChecked} onChange={e => setSensitiveChecked(e.target.checked)} />
            <span>민감정보(초음파 이미지) 처리 동의 <Required>(필수)</Required></span>
          </ConsentCheck>
          <ConsentCheck>
            <input type="checkbox" checked={privacyChecked} onChange={e => setPrivacyChecked(e.target.checked)} />
            <span>개인정보 수집·이용 동의 <Required>(필수)</Required></span>
          </ConsentCheck>
          <TermsRow>
            <TextButton type="button" aria-expanded={showTerms} aria-controls="he-consent-terms" onClick={() => setShowTerms(v => !v)}>
              동의서 내용 {showTerms ? '접기' : '보기'}
            </TextButton>
            <TextButton as="a" href={PATH.INFO_PRIVACY} target="_blank" rel="noopener noreferrer">개인정보 처리방침</TextButton>
          </TermsRow>
          {showTerms &&
            <PrivacyInfo id="he-consent-terms">
              <b>1. 민감정보 처리 (필수)</b>
              <p>{hospitalName}은(는) 서비스 제공을 위해 아래 정보를 처리합니다.</p>
              <ul>
                <li>항목: 태아 초음파 이미지</li>
                <li>목적: {isBabyfaceAvailable ? '클리어페이스, 베이비페이스' : '클리어페이스'} 변환·보관·전송</li>
                <li>기간: 5년 (재다운로드 지원, 요청 시 파기)</li>
              </ul>
              <b>2. 개인정보 수집·이용 (필수)</b>
              <p>알레시오(주)는 이미지 전송을 위해 아래 정보를 수집합니다.</p>
              <ul>
                <li>항목: 휴대전화번호</li>
                <li>목적: 이미지 전송 및 재다운로드</li>
                <li>기간: 5년 (요청 시 파기)</li>
              </ul>
              <p>동의를 거부할 수 있으며, 거부 시 이미지를 받을 수 없습니다.</p>
              <b>처리 위탁 안내</b>
              <p>{hospitalName}은(는) 주식회사 알레시오에 이미지 변환·보관·전송 업무를 위탁합니다.</p>
            </PrivacyInfo>
          }
        </ConsentCard>
        <ConsentButton forwardedAs="button" type="button" onClick={handleConsentClick} disabled={!allChecked}>
          동의하고 이미지 받기
        </ConsentButton>
        {!allChecked && <Hint>필수 항목에 모두 동의해 주세요</Hint>}
      </PageTemplate>
    );
  }
  else if (step === 'phone') {
    return (
      <PageTemplate title="전화번호 입력">
        <Headline>전화번호를 입력해 주세요</Headline>
        <Lead>
          💝 병원에 있는 소중한 아기 사진을 가져오고 있어요.<br />
          {isBabyfaceAvailable ? '베이비페이스 결과를 카톡으로 전송해 드릴게요!' : '처리 완료 후 카톡으로 고화질 이미지를 전송해 드릴게요!'}
        </Lead>
        <Field>
          <Label htmlFor="he-phone">휴대폰 번호</Label>
          <Input
            id="he-phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="010-2345-6789"
            value={phoneNumber}
            invalid={!!phoneError}
            aria-invalid={!!phoneError}
            aria-describedby="he-phone-error"
            onChange={(e) => {
              setPhoneNumber(formatPhone(e.target.value));
              setPhoneError('');
            }}
          />
          <ErrorText id="he-phone-error" role="alert">{phoneError}</ErrorText>
        </Field>
        <BenefitCard>
          <BenefitTitle>🎁 카톡으로 받는 특별혜택</BenefitTitle>
          <BenefitList items={benefits} />
        </BenefitCard>
        <ConsentButton forwardedAs="button" type="button" onClick={handlePhoneSubmit} disabled={isLoading} aria-busy={isLoading}>
          {isLoading ? <ClipLoader color={'#ffffff'} size={24} /> : '카톡으로 받기'}
        </ConsentButton>
      </PageTemplate>
    );
  } else if (step === 'success') {
    return (
      <PageTemplate title="전송 완료">
        <Success>
          <SuccessBadge aria-hidden="true">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </SuccessBadge>
          <Headline>카카오톡으로 전송해 드릴게요</Headline>
          <Lead><b>{maskPhone(phone)}</b> 번호로<br />곧 카카오톡 메시지가 도착해요.</Lead>
        </Success>
        <BenefitCard>
          <BenefitTitle>💌 전송 내용</BenefitTitle>
          <BenefitList items={deliveries} />
        </BenefitCard>
      </PageTemplate>
    );
  }
}

const BenefitList = ({ items }) => (
  <Benefits>
    {items.map(([title, sub]) => (
      <li key={title}>
        <CheckIcon />
        <span>{title}{sub && <small>{sub}</small>}</span>
      </li>
    ))}
  </Benefits>
);

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <circle cx="10" cy="10" r="10" fill="#FFE3DE" />
    <path d="M6 10.2l2.7 2.7L14 7.6" fill="none" stroke="#ff6452" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
    <circle cx="8" cy="8" r="7.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 7.2v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="8" cy="4.9" r="0.95" fill="currentColor" />
  </svg>
);

export default React.memo(HePrivacyConsent);

const PAGE_TITLE_CLEARFACE = '클리어페이스';
const PAGE_TITLE_BABYFACE = '베이비페이스';

// 브랜드 orangePink(#ff6452) 기반 따뜻한 톤
const INK = '#191F28';
const INK_SUB = '#4E5968';
const INK_MUTED = '#8B95A1';
const CREAM = '#FFF5F2';
const PEACH = '#FFE3DE';
const LINE = '#EEE7E5';

const Hero = styled.div`
  margin: -1.25rem -1.25rem 0;
  padding: 1.25rem 1.25rem 0.75rem;
  background: linear-gradient(180deg, ${CREAM} 0%, ${CREAM} 72%, #fff 100%);
`;

const Headline = styled.h2`
  margin: 0 0 0.375rem;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: -0.04em;
  color: ${INK};
  word-break: keep-all;
`;

const SectionTitle = styled(Headline)`
  margin-top: 1rem;
  font-size: 1.125rem;
`;

const Lead = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: ${INK_SUB};
  word-break: keep-all;
  b {
    font-size: inherit;
    font-weight: 700;
    color: ${(props) => props.theme.color.orangePink};
  }
`;

const Photo = styled.img`
  display: block;
  width: 100%;
  margin-top: 0.875rem;
  padding: 0.375rem 0.375rem 0;
  border-radius: 1rem;
  background: #fff;
  box-shadow: 0 6px 20px rgba(255, 100, 82, 0.12), 0 1px 3px rgba(25, 31, 40, 0.06);
`;

const Warning = styled.p`
  display: flex;
  align-items: center;
  gap: 0.3125rem;
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6B7684;
  svg {
    flex-shrink: 0;
    color: ${(props) => props.theme.color.orangePink};
  }
  span, strong {
    font-size: inherit;
  }
  strong {
    font-weight: 700;
    color: ${(props) => props.theme.color.orangePink};
  }
`;

const ConsentCard = styled.div`
  margin-bottom: 1rem;
  padding: 0.375rem 0.875rem 0.375rem;
  border: 1px solid ${LINE};
  border-radius: 1rem;
  background: #fff;
`;

const CHECK_SVG = encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M6.5 12.5l3.5 3.5 7.5-8" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>');

const ConsentCheck = styled.label`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-height: 2.5rem;
  font-size: 0.875rem;
  color: ${INK};
  cursor: pointer;
  ${(props) => props.all && `
    margin: 0 -0.5rem 0.125rem;
    padding: 0 0.5rem;
    min-height: 2.75rem;
    border-radius: 0.625rem;
    font-size: 0.9375rem;
    font-weight: 700;
    background: ${props.checked ? CREAM : '#F7F8F9'};
    transition: background 0.2s ease-out;
  `}
  input {
    appearance: none;
    -webkit-appearance: none;
    flex-shrink: 0;
    width: 1.375rem;
    height: 1.375rem;
    margin: 0;
    border-radius: 50%;
    border: 1.5px solid #D1D6DB;
    background: #fff center / 100% no-repeat;
    cursor: pointer;
    transition: background-color 0.15s ease-out, border-color 0.15s ease-out;
  }
  input:checked {
    border-color: ${(props) => props.theme.color.orangePink};
    background-color: ${(props) => props.theme.color.orangePink};
    background-image: url("data:image/svg+xml,${CHECK_SVG}");
  }
  input:focus-visible {
    outline: 2px solid ${(props) => props.theme.color.orangePink};
    outline-offset: 2px;
  }
  & > span {
    font-size: 0.875rem;
  }
`;

const Required = styled.span`
  margin-left: 0.125rem;
  font-size: 0.8125rem;
  color: ${INK_MUTED};
`;

const Notes = styled.div`
  display: grid;
  gap: 0.25rem;
  margin: 0.5rem 0 0.875rem;
`;

const TermsRow = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0.25rem 0 0.375rem 2rem;
`;

const TextButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.75rem;
  color: ${INK_MUTED};
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
`;

const PrivacyInfo = styled.div`
  margin-bottom: 0.5rem;
  padding: 5.5px 12px;
  border-radius: 0.5rem;
  background-color: #F2F4F6;
  color: #A7AEB9;
  font-size: 0.6875rem;
  line-height: 1.5;
  b {
    display: block;
    margin-top: 0.5rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: #666666;
  }
  b:first-child {
    margin-top: 0;
  }
  ul {
    margin: 0;
    padding-inline-start: 1rem;
  }
  p {
    margin: 0.5rem 0 0;
    font-size: inherit;
  }
  ul li {
    font-size: 0.6875rem;
    line-height: 1.5;
  }
`;

const ConsentButton = styled(Button)`
  height: 3.5rem;
  border: none;
  border-radius: 0.875rem;
  font-family: inherit;
  font-size: 1.0625rem;
  background: ${(props) => props.theme.color.orangePink};
  box-shadow: ${(props) => (props.disabled ? 'none' : '0 8px 20px rgba(255, 100, 82, 0.28)')};
  opacity: ${(props) => (props.disabled ? 0.35 : 1)};
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  transition: opacity 0.2s ease-out, box-shadow 0.2s ease-out, transform 0.1s ease-out;
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  &:focus-visible {
    outline: 2px solid ${INK};
    outline-offset: 3px;
  }
`;

const Hint = styled.p`
  margin: 0.625rem 0 0;
  text-align: center;
  font-size: 0.8125rem;
  color: ${INK_MUTED};
`;

const Field = styled.div`
  margin: 1.75rem 0 0.75rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${INK_SUB};
`;

const Input = styled.input`
  width: 100%;
  height: 3.5rem;
  padding: 0 1rem;
  border-radius: 0.875rem;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  font-variant-numeric: tabular-nums;
  color: ${INK};
  caret-color: ${(props) => props.theme.color.orangePink};
  background: #F7F8F9;
  border: 1.5px solid ${(props) => (props.invalid ? '#E5484D' : 'transparent')};
  transition: border-color 0.15s ease-out, background 0.15s ease-out;
  &:focus {
    outline: none;
    background: #fff;
    border-color: ${(props) => (props.invalid ? '#E5484D' : props.theme.color.orangePink)};
  }
  &::placeholder {
    font-weight: 400;
    color: ${INK_MUTED};
  }
`;

const ErrorText = styled.p`
  min-height: 1.25rem;
  margin: 0.375rem 0 0;
  font-size: 0.8125rem;
  color: #E5484D;
`;

const BenefitCard = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem 1.125rem 1.125rem;
  border-radius: 1rem;
  background: ${CREAM};
`;

const BenefitTitle = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: ${INK};
`;

const Benefits = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.875rem;
    line-height: 1.45;
    color: ${INK};
  }
  li + li {
    margin-top: 0.625rem;
  }
  li > span {
    font-size: 0.875rem;
  }
  svg {
    flex-shrink: 0;
    margin-top: 0.0625rem;
  }
  small {
    display: block;
    margin-top: 0.0625rem;
    font-size: 0.75rem;
    color: ${INK_MUTED};
  }
`;

const Success = styled.div`
  padding: 1.5rem 0 1.75rem;
  text-align: center;
`;

const SuccessBadge = styled.div`
  display: grid;
  place-items: center;
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1rem;
  border-radius: 50%;
  background: ${(props) => props.theme.color.orangePink};
  box-shadow: 0 0 0 8px ${PEACH};
`;
